import React, {createContext, useCallback, useContext, useMemo, useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import {IconButton} from '@/shared/ui/button';
import {ButtonLayoutContext} from '@/shared/ui/button-layout';
import {AppText} from '@/shared/ui/text';
import {spacing, useTheme} from '@/shared/ui/theme';

/**
 * Инспектор — пристыкованная к правому краю окна панель с ВЫБРАННОЙ записью.
 *
 * ## Что это заменило
 *
 * Плавающую карточку 400pt шириной рядом с таблицей — с заглушкой «Выберите
 * сотрудника», когда ничего не выбрано. Заглушка стояла ВСЕГДА, и это она отнимала у
 * таблицы треть окна на 1440pt: «Проходная — главный вход» превращалась в
 * «Проходна…», хотя справа была пустая коробка с иконкой.
 *
 * Здесь панель — это shadcn `Sheet side="right"` без затемнения: она существует,
 * только пока есть что показать, стоит на всю высоту рабочей области, у неё свой
 * заголовок с крестиком и своя прокрутка. Пока строка не выбрана, таблице отдаётся
 * всё окно. Это то же самое, что делает Gmail с письмом, Notion с side peek и Linear с
 * задачей: список остаётся под рукой, запись читается рядом.
 *
 * ## Как страница узнаёт, что показать
 *
 * Через контекст. Выбор строки живёт внутри списка (`PeopleList`, `DeviceList`), а
 * панель должна стоять СНАРУЖИ страничной прокрутки — сестрой колонки с содержимым,
 * а не потомком таблицы. Список поэтому не рисует панель сам, а регистрирует запись у
 * хозяина (`useInspectorHost`), и рисует её `Page`. См. `SplitView`.
 */
export type InspectorEntry = {
  /** Ключ выбранной записи. Новый ключ — новая запись в панели. */
  key: string;
  /** Заголовок панели: имя человека, имя терминала. */
  title?: string;
  /** Тело панели. Функция, а не узел: читается на каждом рендере хозяина. */
  render: () => React.ReactNode;
  onClose: () => void;
};

type SetEntry = (entry: InspectorEntry | null) => void;

/*
  В контексте лежит ТОЛЬКО `set`, и его ссылка стабильна. Положить туда и `entry`
  значило бы менять значение контекста на каждую смену записи — а список, который
  на это значение подписан, перерегистрировал бы запись заново, и страница с ним
  ходили бы по кругу.
*/
const InspectorContext = createContext<SetEntry | null>(null);

/** Хозяин панели — тот, кто её рисует. Даёт `entry` и способ её сменить. */
export function useInspectorHostState(): {entry: InspectorEntry | null; set: SetEntry} {
  const [entry, setEntry] = useState<InspectorEntry | null>(null);
  const set = useCallback<SetEntry>(next => {
    setEntry(prev => {
      if (prev === next) {
        return prev;
      }
      // Тот же ключ и та же функция — та же запись; не дёргать хозяина зря.
      if (prev && next && prev.key === next.key && prev.render === next.render) {
        return prev;
      }
      return next;
    });
  }, []);
  return useMemo(() => ({entry, set}), [entry, set]);
}

export const InspectorProvider = InspectorContext.Provider;

/** Списку — чтобы зарегистрировать запись. `null`, если страница панели не держит. */
export function useInspectorHost(): SetEntry | null {
  return useContext(InspectorContext);
}

/** Ширина панели: карточка человека или терминала читается в 420pt. */
export const INSPECTOR_WIDTH = 420;

const styles = StyleSheet.create({
  root: {
    width: INSPECTOR_WIDTH,
    borderLeftWidth: StyleSheet.hairlineWidth,
  },
  head: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    height: 56,
    paddingLeft: spacing(5),
    paddingRight: spacing(3),
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  title: {flex: 1, minWidth: 0},
  body: {padding: spacing(5), paddingBottom: spacing(10)},
});

export function InspectorPanel({entry}: {entry: InspectorEntry}) {
  const {t} = useTranslation();
  const p = useTheme();

  return (
    <View
      testID="inspector"
      style={[styles.root, {backgroundColor: p.card, borderLeftColor: p.border}]}>
      <View style={[styles.head, {borderBottomColor: p.border}]}>
        <AppText variant="heading" numberOfLines={1} style={styles.title}>
          {entry.title ?? ''}
        </AppText>
        <IconButton
          testID="inspector-close"
          icon="close"
          label={t('actions.close')}
          size={32}
          onPress={entry.onClose}
        />
      </View>
      <ScrollView contentContainerStyle={styles.body} showsVerticalScrollIndicator>
        {/* Панель шириной с телефон — и кнопки в ней растягиваются, как на телефоне.
            Новый ключ — новое поддерево: карточка другого человека не должна
            унаследовать состояние (раскрытые секции, черновик) предыдущей. */}
        <ButtonLayoutContext.Provider value="stretch">
          <React.Fragment key={entry.key}>{entry.render()}</React.Fragment>
        </ButtonLayoutContext.Provider>
      </ScrollView>
    </View>
  );
}
